import { useEffect, useRef } from "react";
import * as THREE from "three";

interface FractalShaderProps {
  scrollProgress?: number;
}

export default function FractalShader({ scrollProgress = 0 }: FractalShaderProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef(scrollProgress);

  useEffect(() => {
    scrollRef.current = scrollProgress;
  }, [scrollProgress]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const state = {
      dpr: Math.min(window.devicePixelRatio, 1.5),
      cubeSize: 4.9,
      speed: 0.8,
      fractalScale: 1.0,
      distortion: 0.0,
      glow: 0.72,
    };

    // Mouse interaction state
    let isDragging = false;
    let previousMousePosition = { x: 0, y: 0 };
    const targetRotation = { x: 0, y: 0 };
    const currentRotation = { x: 0, y: 0 };

    const renderer = new THREE.WebGLRenderer({ antialias: false, alpha: true });
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setPixelRatio(state.dpr);
    container.appendChild(renderer.domElement);

    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const scene = new THREE.Scene();

    const uniforms = {
      iTime: { value: 0 },
      iResolution: {
        value: new THREE.Vector2(container.clientWidth, container.clientHeight),
      },
      u_mouseRot: { value: new THREE.Vector2(0, 0) },
      u_speed: { value: state.speed },
      u_cubeSize: { value: state.cubeSize },
      u_scale: { value: state.fractalScale },
      u_distortion: { value: state.distortion },
      u_glow: { value: state.glow },
      u_colorOffset: { value: new THREE.Vector3(0.5, 1.5, 3.0) },
    };

    const fragmentShader = `
      uniform vec2 iResolution;
      uniform float iTime;
      uniform vec2 u_mouseRot;

      uniform float u_speed;
      uniform float u_cubeSize;
      uniform float u_scale;
      uniform float u_distortion;
      uniform float u_glow;
      uniform vec3 u_colorOffset;

      // Slab test against a pair of parallel planes with given 2D normal.
      vec2 slabTest2D(vec2 normal, float radius, vec2 ro2, vec2 rd2, vec2 tRange) {
        float dRd = dot(rd2, normal);
        float dRo = dot(ro2, normal);
        if (abs(dRd) < 1e-5) {
          if (abs(dRo) > radius) return vec2(1.0, -1.0);
          return tRange;
        }
        float t1 = (-radius - dRo) / dRd;
        float t2 = ( radius - dRo) / dRd;
        if (t1 > t2) { float tmp = t1; t1 = t2; t2 = tmp; }
        return vec2(max(tRange.x, t1), min(tRange.y, t2));
      }

      // Hexagonal prism intersection: hexagon lies in the XZ plane, extruded along Y.
      vec2 intersectHexPrism(vec3 ro, vec3 rd, float radius, float halfHeight) {
        vec2 tRange = vec2(-1e20, 1e20);

        tRange = slabTest2D(vec2(1.0, 0.0),        radius, ro.xz, rd.xz, tRange);
        tRange = slabTest2D(vec2(0.5, 0.8660254),  radius, ro.xz, rd.xz, tRange);
        tRange = slabTest2D(vec2(-0.5, 0.8660254), radius, ro.xz, rd.xz, tRange);

        if (abs(rd.y) < 1e-5) {
          if (abs(ro.y) > halfHeight) return vec2(-1.0);
        } else {
          float t1 = (-halfHeight - ro.y) / rd.y;
          float t2 = ( halfHeight - ro.y) / rd.y;
          if (t1 > t2) { float tmp = t1; t1 = t2; t2 = tmp; }
          tRange.x = max(tRange.x, t1);
          tRange.y = min(tRange.y, t2);
        }

        if (tRange.x > tRange.y || tRange.y < 0.0) return vec2(-1.0);
        return vec2(max(tRange.x, 0.0), tRange.y);
      }

      // 2D hexagon field (apothem distance). Positive outside a hex, negative inside.
      float hex2D(vec2 p) {
        p = abs(p);
        return max(dot(p, vec2(0.8660254, 0.5)), p.y);
      }

      mat2 rotate(float a) {
        float s = sin(a), c = cos(a);
        return mat2(c, -s, s, c);
      }

      void mainImage(out vec4 fragColor, in vec2 fragCoord) {
        vec2 uv = (fragCoord.xy * 2.0 - iResolution.xy) / iResolution.y;

        vec3 ro = vec3(0.0, 0.0, -22.0);
        vec3 rd = normalize(vec3(uv, 2.2));

        float time = iTime * 0.3 * u_speed;

        mat2 rotX = rotate(u_mouseRot.y);
        mat2 rotY = rotate(time + u_mouseRot.x);

        ro.yz *= rotX;
        ro.xz *= rotY;
        rd.yz *= rotX;
        rd.xz *= rotY;

        vec2 hit = intersectHexPrism(ro, rd, u_cubeSize, u_cubeSize);

        vec3 accumCol = vec3(0.0);

        if (hit.y > 0.0) {
          float t = hit.x;
          float tMax = hit.y;

          vec3 colorBase = u_colorOffset + (iTime * 1.4) + 0.09;

          for (int i = 0; i < 100; i++) {
            if (t >= tMax) break;

            vec3 pos = ro + rd * t;
            vec3 q = pos * u_scale;

            // Tile space into cells, then measure hexagon fields on the three orthogonal planes.
            vec3 pWrap = fract(q * 0.25) * 4.0 - 2.0;

            float hxy = hex2D(pWrap.xy);
            float hyz = hex2D(pWrap.yz);
            float hxz = hex2D(pWrap.xz);

            // Combine three hex fields into a lattice of hex "pipes" running along each axis.
            float dStruct = min(hxy, min(hyz, hxz)) - 1.0;

            float distortion = 0.0;
            if (u_distortion > 0.001) {
              distortion = u_distortion * dot(sin(q * 1.2), cos(q.yzx * 1.0));
            }

            float shape = abs(dStruct) + distortion;

            float stepSize = max(0.01, 0.7 * abs(shape));
            stepSize = min(stepSize, tMax - t);

            // Pastel-neon palette: high base + gentle amplitude keeps hues soft & candy-colored.
            vec3 colorPalette = 0.62 + 0.38 * cos(0.15 * float(i) + colorBase);

            float edgeFade = smoothstep(0.0, 0.5, t - hit.x);

            // Boosted weight for a brighter neon glow along the hex edges.
            float weight = stepSize * edgeFade * (0.062 / (abs(shape) + 0.075));
            accumCol += colorPalette * weight;

            t += stepSize;
          }
        }

        // Soft rainbow-ish vignette that reinforces the neon halo.
        vec3 vignette = vec3(0.35, 0.55, 0.75) / max(length(uv) * 9.8, 0.001);
        accumCol += vignette * u_glow;

        // Slightly softer tone map keeps the pastel character while allowing neon peaks.
        vec3 finalColor = tanh(accumCol * 1.55);

        if (hit.y < 0.0) finalColor = vec3(0.0);

        fragColor = vec4(finalColor, 1.0);
      }

      void main() {
        mainImage(gl_FragColor, gl_FragCoord.xy);
      }
    `;

    const material = new THREE.ShaderMaterial({
      fragmentShader,
      uniforms,
      depthWrite: false,
      depthTest: false,
    });

    const geometry = new THREE.PlaneGeometry(2, 2);
    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    // Pointer interaction on the canvas
    const canvas = renderer.domElement;
    canvas.style.cursor = "grab";
    canvas.style.display = "block";
    canvas.style.touchAction = "none";
    canvas.style.width = "100%";
    canvas.style.height = "100%";

    const onPointerDown = (e: PointerEvent) => {
      isDragging = true;
      previousMousePosition = { x: e.clientX, y: e.clientY };
      canvas.style.cursor = "grabbing";
      canvas.setPointerCapture(e.pointerId);
    };

    const onPointerUp = (e: PointerEvent) => {
      isDragging = false;
      canvas.style.cursor = "grab";
      try {
        canvas.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!isDragging) return;
      const deltaMove = {
        x: e.clientX - previousMousePosition.x,
        y: e.clientY - previousMousePosition.y,
      };

      targetRotation.x -= deltaMove.x * 0.01;
      targetRotation.y += deltaMove.y * 0.01;

      targetRotation.y = Math.max(
        -Math.PI / 2,
        Math.min(Math.PI / 2, targetRotation.y)
      );

      previousMousePosition = { x: e.clientX, y: e.clientY };
    };

    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointerup", onPointerUp);
    canvas.addEventListener("pointercancel", onPointerUp);
    canvas.addEventListener("pointermove", onPointerMove);

    // Resize handling
    const handleResize = () => {
      const width = container.clientWidth;
      const height = container.clientHeight;
      renderer.setSize(width, height);
      uniforms.iResolution.value.set(width, height);
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(container);
    window.addEventListener("resize", handleResize);

    // Neon pastel palette waypoints traversed by scroll progress
    const palettes: [number, number, number][] = [
      [0.5, 1.5, 3.0], // cotton candy pink
      [1.5, 3.2, 2.0], // mint sorbet
      [3.0, 2.0, 4.5], // soft lavender
      [2.6, 4.5, 1.6], // peach + lemon
      [1.0, 2.4, 4.0], // sky bubblegum
      [0.5, 1.5, 3.0], // loop back
    ];

    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

    // Smoothed color offset for buttery transitions
    const currentColor = { r: 0.5, g: 1.5, b: 3.0 };

    // Animation loop
    const clock = new THREE.Clock();
    let rafId = 0;

    const animate = () => {
      rafId = requestAnimationFrame(animate);

      uniforms.iTime.value = clock.getElapsedTime();

      currentRotation.x += (targetRotation.x - currentRotation.x) * 0.1;
      currentRotation.y += (targetRotation.y - currentRotation.y) * 0.1;
      uniforms.u_mouseRot.value.set(currentRotation.x, currentRotation.y);

      // Scroll-driven color palette interpolation
      const s = Math.max(0, Math.min(1, scrollRef.current));
      const segmentCount = palettes.length - 1;
      const scaled = s * segmentCount;
      const idx = Math.min(Math.floor(scaled), segmentCount - 1);
      const localT = scaled - idx;
      const a = palettes[idx];
      const b = palettes[idx + 1];
      const tr = lerp(a[0], b[0], localT);
      const tg = lerp(a[1], b[1], localT);
      const tb = lerp(a[2], b[2], localT);

      // Smooth toward target
      currentColor.r += (tr - currentColor.r) * 0.08;
      currentColor.g += (tg - currentColor.g) * 0.08;
      currentColor.b += (tb - currentColor.b) * 0.08;
      uniforms.u_colorOffset.value.set(
        currentColor.r,
        currentColor.g,
        currentColor.b
      );

      renderer.render(scene, camera);
    };

    animate();

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      window.removeEventListener("resize", handleResize);
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointerup", onPointerUp);
      canvas.removeEventListener("pointercancel", onPointerUp);
      canvas.removeEventListener("pointermove", onPointerMove);

      geometry.dispose();
      material.dispose();
      renderer.dispose();

      if (canvas.parentNode) {
        canvas.parentNode.removeChild(canvas);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="tw-relative tw-w-full tw-h-full tw-overflow-hidden tw-bg-black"
    />
  );
}